import { useEffect } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import { useEgresosStore } from '../../../shared/store/egresos.store';
import type { Egreso } from '../../../shared/types';

// Importar el hook de Firebase directamente
import { useEgresosFirebase } from './useEgresosFirebase';

export const useEgresosOptimistic = () => {
  const { user } = useAuth();
  
  // Debug: Log del usuario actual
  console.log('👤 useEgresosOptimistic - Usuario actual:', { 
    uid: user?.uid, 
    email: user?.email 
  });
  const {
    egresos: cachedEgresos,
    isLoading: cacheLoading,
    error: cacheError,
    addEgresoOptimistic,
    removeEgresoOptimistic,
    setEgresos,
    setError,
    clearError
  } = useEgresosStore();

  // Hook de Firebase para sincronización
  const { 
    egresos: firebaseEgresos, 
    loading: firebaseLoading,
    error: firebaseError,
    createEgreso: createFirebaseEgreso,
    deleteEgreso: deleteFirebaseEgreso
  } = useEgresosFirebase();

  // Sincronizar datos de Firebase con cache local al inicializar
  useEffect(() => {
    if (firebaseEgresos && Array.isArray(firebaseEgresos)) {
      // Filtrar solo egresos reales de Firebase (no temporales)
      const realFirebaseEgresos = firebaseEgresos.filter(eg => !eg.id.startsWith('temp_'));
      
      // Obtener egresos temporales del cache
      const tempEgresos = cachedEgresos.filter(eg => eg.id.startsWith('temp_'));
      
      // Combinar: egresos de Firebase + egresos temporales
      const combinedEgresos = [...realFirebaseEgresos, ...tempEgresos];
      
      // Solo actualizar si la combinación es diferente al cache actual
      if (JSON.stringify(combinedEgresos.map(e => e.id).sort()) !== 
          JSON.stringify(cachedEgresos.map(e => e.id).sort())) {
        setEgresos(combinedEgresos);
      }
    }
  }, [firebaseEgresos, setEgresos, cachedEgresos]);

  // Manejar errores de Firebase
  useEffect(() => {
    if (firebaseError) {
      setError(firebaseError);
    }
  }, [firebaseError, setError]);

  // Función para crear egreso con actualización optimista
  const createEgreso = async (egresoData: Omit<Egreso, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    if (!user) {
      const error = 'Usuario no autenticado';
      setError(error);
      throw new Error(error);
    }

    clearError();
    
    // Datos completos para el egreso
    const completeEgresoData = {
      ...egresoData,
      userId: user.uid,
      fecha: egresoData.fecha || new Date(),
    };

    // 1. Agregar inmediatamente al cache local (OPTIMISTIC UPDATE)
    const tempEgreso = addEgresoOptimistic(completeEgresoData);

    try {
      // 2. Guardar en Firebase en segundo plano
      const realEgresoId = await createFirebaseEgreso({
        concepto: completeEgresoData.concepto,
        monto: completeEgresoData.monto,
        fecha: completeEgresoData.fecha,
        categoria: completeEgresoData.categoria,
        descripcion: completeEgresoData.descripcion || '',
      });
      
      // 3. Cuando Firebase confirma, remover el temporal inmediatamente
      // El useEffect se encargará de agregar la versión real de Firebase
      if (realEgresoId) {
        removeEgresoOptimistic(tempEgreso.id);
      }
      
      return realEgresoId || tempEgreso.id;
    } catch (error) {
      
      // Si falla Firebase, remover del cache local
      removeEgresoOptimistic(tempEgreso.id);
      
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      setError(`Error guardando egreso: ${errorMessage}`);
      throw error;
    }
  };

  // Función para eliminar con actualización optimista
  const deleteEgreso = async (id: string) => {
    // 1. Remover inmediatamente del cache local
    removeEgresoOptimistic(id);

    try {
      // 2. Eliminar de Firebase en segundo plano (solo si no es temporal)
      if (!id.startsWith('temp_')) {
        await deleteFirebaseEgreso(id);
      }
    } catch {
      setError('Error eliminando egreso de Firebase');
    }
  };

  // Combinar loading states
  const isLoading = cacheLoading || firebaseLoading;
  const error = cacheError || firebaseError;

  return {
    egresos: cachedEgresos,
    isLoading,
    error,
    createEgreso,
    deleteEgreso,
    clearError,
  };
};